import { useT } from '../context/LanguageContext';
import React, { useEffect, useState } from 'react';
import { Map as MapIcon, X } from 'lucide-react';
import { useContent } from '../context/ContentContext';
import { EditButton } from './admin/EditorShell';
import { SchemaEditor } from './admin/SchemaEditor';
import { pictureUrl } from '../lib/api';

const MAP_SCHEMA = [
  { key: 'heading', label: 'Overskrift' },
  { key: 'intro', label: 'Tekst under overskrifta' },
  { key: 'embed', label: 'Google Maps-innbygging (lim inn <iframe>-koden eller src-lenka)', type: 'textarea', rows: 3 },
  { key: 'arenaTitle', label: 'Arenakart: tittel' },
  { key: 'arenaText', label: 'Arenakart: tekst', type: 'textarea', rows: 3 },
  { key: 'arenaImage', label: 'Arenakart: bilete', type: 'image' },
];
const mapSrc = (embed, fallback) => { const m = (embed || '').match(/src=["']([^"']+)["']/); const u = m ? m[1] : (embed || '').trim(); return /^https:\/\/www\.google\.com\/maps\/embed/.test(u) ? u : fallback; };
import { Navigation } from 'lucide-react';

const MAP_EMBED = 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1963.6189001483008!2d4.906481476625856!3d60.51751511535264!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x463d21d04cb95197%3A0xbaebd716ae8cb814!2sRefuel%20Weekend!5e0!3m2!1sno!2sno!4v1790676425216!5m2!1sno!2sno';

const mapLinks = [
  {
    id: 'google',
    label: 'Google Maps',
    sub: 'Opne i Google Maps',
    href: 'https://maps.app.goo.gl/xUdrLWhmCGVGYUmW7',
    img: 'https://customer-assets-jai6qajn.emergentagent.net/job_weekend-refuel/artifacts/rtojr728_Google_Maps_icon_%282020%29.svg.webp',
    imgCls: 'h-11 w-auto',
  },
  {
    id: 'apple',
    label: 'Apple Maps',
    sub: 'Opne i Apple Maps',
    href: 'https://maps.apple/p/dUCRbm6EbH1CvI',
    img: 'https://customer-assets-jai6qajn.emergentagent.net/job_weekend-refuel/artifacts/2ga4qenm_intro_icon__dfyvjc1ohbcm_large.png',
    imgCls: 'h-12 w-12 rounded-xl',
  },
  {
    id: 'waze',
    label: 'Waze GPS',
    sub: 'Naviger med Waze',
    href: 'https://ul.waze.com/ul?place=ChIJWVNwV58hPUYRLLPF0mYM-RI&ll=60.51762060%2C4.90912970&navigate=yes&utm_campaign=default&utm_source=waze_website&utm_medium=lm_share_location',
    img: 'https://customer-assets-jai6qajn.emergentagent.net/job_weekend-refuel/artifacts/vkodruwm_waze_app_icon-logo_brandlogos.net_l82da.png',
    imgCls: 'h-12 w-12 rounded-xl',
  },
];

const LocationMap = () => {
  const [mapOpen, setMapOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const { content } = useContent();
  const m = content.map || {};
  const arenaImg = m.arenaImage ? pictureUrl(m.arenaImage) : '/img/arena-kart.webp';
  useEffect(() => {
    if (!mapOpen) return;
    const onKey = (e) => e.key === 'Escape' && setMapOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mapOpen]);
  const { t } = useT();
  return (
    <section id="kart" className="bg-black py-20 md:py-28">
      <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
        <h2 className="text-5xl md:text-7xl font-bold text-white tracking-tighter mb-3" style={{ fontFamily: "'Oktah Round', sans-serif" }}>
          {t(m.heading || 'Kor er vi?')}
        </h2>
        <div className="flex items-start justify-between gap-4 mb-10"><p className="text-white/60 text-lg">{t(m.intro || 'Trykk på kartet under for å få vegskildring til Refuel Weekend')}</p><EditButton onClick={() => setEditing(true)} testId="edit-map-button" /></div>

        <div className="max-w-4xl mx-auto rounded-3xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50" data-testid="map-embed-container">
          <iframe
            title="Refuel Weekend – Hjeltefjorden Arena"
            src={mapSrc(m.embed, MAP_EMBED)}
            className="w-full aspect-[16/9] md:aspect-[4/3] block"
            style={{ border: 0, minHeight: 320 }}
            allowFullScreen
            loading="lazy"
            referrerPolicy="strict-origin-when-cross-origin"
            data-testid="map-iframe"
          />
        </div>

        <div className="mt-8 grid sm:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto" data-testid="map-links">
          {mapLinks.map((l) => (
            <a
              key={l.id}
              href={l.href}
              target="_blank"
              rel="noreferrer noopener"
              data-testid={`map-link-${l.id}`}
              className="group flex items-center gap-4 p-4 rounded-2xl bg-[#0f0a17] border border-white/10 hover:border-[#ff2e2e] hover:shadow-lg hover:shadow-[#ff2e2e]/20 transition-[border-color,box-shadow,transform] hover:-translate-y-0.5 text-left"
            >
              <div className="w-14 h-14 rounded-2xl bg-white flex items-center justify-center shrink-0 overflow-hidden">
                <img src={l.img} alt={`${l.label} ikon`} loading="lazy" decoding="async" className={`${l.imgCls} object-contain`} />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-bold text-base leading-tight">{l.label}</p>
                <p className="text-white/50 text-xs mt-0.5">{t(l.sub)}</p>
              </div>
              <Navigation size={18} className="text-[#ff2e2e] shrink-0 transition-transform group-hover:translate-x-0.5" />
            </a>
          ))}
        </div>

        <div className="mt-10 md:mt-14 grid md:grid-cols-[1fr_auto] items-center gap-6 rounded-3xl bg-[#120c1b] border border-white/5 p-5 md:p-7" data-testid="arena-map-card">
          <div>
            <p className="text-[#ff2e2e] font-bold uppercase tracking-widest text-xs mb-2 inline-flex items-center gap-2"><MapIcon size={14} /> {t('Kart over området')}</p>
            <h3 className="text-2xl md:text-3xl font-bold text-white tracking-tight mb-2" style={{ fontFamily: "'Oktah Round', sans-serif" }}>{m.arenaTitle || 'Hjeltefjorden Arena'}</h3>
            <p className="text-white/60 text-sm md:text-base max-w-xl">{t(m.arenaText || 'Finn fram til hovudsalen, fellesrommet, kiosken, mattelt, symjehall, skulen og parkering. Inngangen er merka med raud pil.')}</p>
          </div>
          <button type="button" onClick={() => setMapOpen(true)} data-testid="arena-map-open" className="group relative w-full md:w-56 aspect-[4/5] rounded-2xl overflow-hidden border border-white/10 hover:border-[#ff2e2e] transition-colors">
            <img src={arenaImg} alt="Skjematisk kart over Hjeltefjorden Arena under Refuel Weekend" loading="lazy" decoding="async" width={1122} height={1402} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
            <span className="absolute bottom-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-black/70 text-white text-xs font-bold">{t('Sjå kartet')}</span>
          </button>
        </div>
        {mapOpen && (
          <div className="fixed inset-0 z-[100] bg-black/95 flex items-center justify-center p-4" onClick={() => setMapOpen(false)} role="dialog" aria-modal="true" data-testid="arena-map-lightbox">
            <button type="button" aria-label="Lukk" onClick={() => setMapOpen(false)} className="absolute top-4 right-4 w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center text-white"><X size={20} /></button>
            <img src={arenaImg} alt="Kart over Hjeltefjorden Arena" onClick={(e) => e.stopPropagation()} className="max-h-[92vh] max-w-full rounded-2xl shadow-2xl" />
          </div>
        )}
      </div>
      {editing && <SchemaEditor contentKey="map" title="Rediger kart og kartinfo" single initial={{ heading: 'Kor er vi?', intro: 'Trykk på kartet under for å få vegskildring til Refuel Weekend', embed: MAP_EMBED, arenaTitle: 'Hjeltefjorden Arena', arenaText: 'Finn fram til hovudsalen, fellesrommet, kiosken, mattelt, symjehall, skulen og parkering. Inngangen er merka med raud pil.', arenaImage: '', ...m }} schema={MAP_SCHEMA} onClose={() => setEditing(false)} />}
    </section>
  );
};

export default LocationMap;
